// web/src/api/hedgeAccounting/HedgeAccountingAPI.ts
import { getTodayFormatted } from '@store/utility/dateUtils';
import { isDef } from '@utils/def';
import { AxiosInstance, AxiosResponse } from 'axios';

import { TransactionAmortSetScheduleParam } from 'pages/HedgeAccountingAdmin/components/TransactionAmortization/types';

export default class HedgeAccountingAPI {
  private client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  async getJournalEntryTypes(): Promise<AxiosResponse> {
    return await this.client.get('/v2/hedge-accounting/journal-entry-type/');
  }

  async getEffectivenessType(): Promise<AxiosResponse> {
    return await this.client.get('/v2/hedge-accounting/effectiveness-type/');
  }

  async getTransactionAmortType(): Promise<AxiosResponse> {
    return await this.client.get('/v2/hedge-accounting/transaction-amort-type');
  }

  async fetchHedgeAmortData(params: {
    transaction_ids?: string[];
    company_entity_ids?: string[];
    run_date?: string;
  }): Promise<AxiosResponse> {
    const requestBody: {
      transaction_ids?: number[];
      company_entity_ids?: string[];
      run_date?: string;
    } = {};

    if (params.transaction_ids && params.transaction_ids.length > 0) {
      requestBody.transaction_ids = params.transaction_ids.map((id) =>
        Number(id),
      );
    }

    if (params.company_entity_ids && params.company_entity_ids.length > 0) {
      requestBody.company_entity_ids = params.company_entity_ids;
    }

    // Add run_date to the request body if provided, otherwise default to today
    if (params.run_date) {
      requestBody.run_date = params.run_date;
    } else {
      // Get today's date in YYYY-MM-DD format
      const today = getTodayFormatted();
      requestBody.run_date = today;
    }

    return await this.client.post(
      '/v2/hedge-accounting/trade-amort/list/',
      requestBody,
    );
  }

  async initialAmortEffectiveRate(params: {
    amort_date: string;
    transaction_id: number;
  }): Promise<AxiosResponse> {
    return await this.client.post(
      '/v2/hedge-accounting/trade-amort/init/',
      params,
    );
  }

  async createTransactionAmort(params: {
    transaction: number;
    amort_date: string;
    je_type: number;
    amort_amt?: number;
    amort_eff_rate?: number;
    amort_comment?: string;
    amort_schedule_transaction: number;
    amort_schedule_type: number;
  }): Promise<AxiosResponse> {
    return await this.client.post('/v2/hedge-accounting/trade-amort/', params);
  }

  async updateTransactionAmort(
    hedge_acc_amort_id: number,
    params: {
      transaction: number;
      amort_date: string;
      je_type: number;
      amort_amt?: number;
      amort_eff_rate?: number;
      amort_comment?: string;
      amort_schedule_transaction: number;
      amort_schedule_type: number;
    },
  ): Promise<AxiosResponse> {
    return await this.client.put(
      `/v2/hedge-accounting/trade-amort/${hedge_acc_amort_id}/`,
      params,
    );
  }

  async patchTransactionAmort(
    hedge_acc_amort_id: number,
    params: Partial<{
      transaction: number;
      amort_date: string;
      je_type: number;
      amort_amt: number;
      amort_eff_rate: number;
      amort_comment: string;
      amort_schedule_transaction: number;
      amort_schedule_type: number;
    }>,
  ): Promise<AxiosResponse> {
    return await this.client.patch(
      `/v2/hedge-accounting/trade-amort/${hedge_acc_amort_id}/`,
      params,
    );
  }

  async deleteTransactionAmort(
    hedge_acc_amort_id: number,
  ): Promise<AxiosResponse> {
    return await this.client.delete(
      `/v2/hedge-accounting/trade-amort/${hedge_acc_amort_id}/`,
    );
  }

  async calculateAmortEffectiveRate(
    hedge_acc_amort_id: number,
    run_date: string,
  ): Promise<AxiosResponse> {
    return await this.client.get(
      `/v2/hedge-accounting/trade-amort/${hedge_acc_amort_id}/calculate-amort-eff-rate/${run_date}/`,
    );
  }

  async fetchTransactionAmortSetSchedule(
    hedge_acc_amort_id: number,
  ): Promise<AxiosResponse> {
    return await this.client.get(
      `/v2/hedge-accounting/trans-amort-set-schedule/${hedge_acc_amort_id}/`,
    );
  }

  async createTransactionAmortSetSchedule(
    params: TransactionAmortSetScheduleParam,
  ): Promise<AxiosResponse> {
    // Create a payload directly matching what the API expects
    const amortId = params.hedge_acc_trans_amort_id ?? params.amort_id;

    // Create the exact payload format required by the API
    const requestBody = {
      hedge_acc_trans_amort_id: amortId,
      period_start_date: params.schedule_data?.[0]?.schedule_date,
      period_end_date: params.schedule_data?.[0]?.period_end_date,
      trans_amort_amount: params.schedule_data?.[0]?.schedule_amt || 0,
    };

    return await this.client.post(
      '/v2/hedge-accounting/trans-amort-set-schedule/',
      requestBody,
    );
  }

  async searchTransactionAmortSetSchedule(params: {
    amort_ids?: number[];
    hedge_acc_trans_amort_id?: number;
  }): Promise<AxiosResponse> {
    // If hedge_acc_trans_amort_id is provided, transform it to match API expectations
    const requestParams = { ...params };

    if (isDef(params.hedge_acc_trans_amort_id)) {
      // The API expects this specific format with a single value
      requestParams.hedge_acc_trans_amort_id = params.hedge_acc_trans_amort_id;
      // Remove amort_ids if hedge_acc_trans_amort_id is provided
      delete requestParams.amort_ids;
    }

    return await this.client.post(
      '/v2/hedge-accounting/trans-amort-set-schedule/search/',
      requestParams,
    );
  }

  async fetchTransactionEffectivenessList(params: {
    transaction_ids?: string[];
    company_entity_ids?: string[];
    run_date?: string;
  }): Promise<AxiosResponse> {
    const requestBody: {
      transaction_ids?: number[];
      company_entity_ids?: string[];
      run_date?: string;
    } = {};

    if (params.transaction_ids && params.transaction_ids.length > 0) {
      requestBody.transaction_ids = params.transaction_ids.map((id) =>
        Number(id),
      );
    }

    if (params.company_entity_ids && params.company_entity_ids.length > 0) {
      requestBody.company_entity_ids = params.company_entity_ids;
    }

    // Add run_date to the request body if provided, otherwise default to today
    if (params.run_date) {
      requestBody.run_date = params.run_date;
    } else {
      // Get today's date in YYYY-MM-DD format
      const today = getTodayFormatted();
      requestBody.run_date = today;
    }

    return await this.client.post(
      '/v2/hedge-accounting/transaction-effectiveness/list/',
      requestBody,
    );
  }

  async createTransactionEffectiveness(data: {
    transaction: number;
    hedge_acc_eff: number;
    hedge_acc_eff_clean_value: boolean;
    hedge_acc_eff_cva_actual: boolean;
    hedge_acc_eff_cva_hypo: boolean;
    hedge_acc_eff_cash_flows: boolean;
    hedge_acc_eff_time_value: boolean;
    hedge_acc_eff_new_date_method: boolean;
    hedge_acc_credit_adj_values_final_balance: boolean;
  }): Promise<AxiosResponse> {
    return await this.client.post(
      '/v2/hedge-accounting/transaction-effectiveness/',
      data,
    );
  }

  async updateTransactionEffectiveness(
    id: number,
    data: {
      transaction: number;
      hedge_acc_eff: number;
      hedge_acc_eff_clean_value: boolean;
      hedge_acc_eff_cva_actual: boolean;
      hedge_acc_eff_cva_hypo: boolean;
      hedge_acc_eff_cash_flows: boolean;
      hedge_acc_eff_time_value: boolean;
      hedge_acc_eff_new_date_method: boolean;
      hedge_acc_credit_adj_values_final_balance: boolean;
    },
  ): Promise<AxiosResponse> {
    return await this.client.put(
      `/v2/hedge-accounting/transaction-effectiveness/${id}/`,
      data,
    );
  }

  async patchTransactionEffectiveness(
    id: number,
    data: Partial<{
      transaction: number;
      hedge_acc_eff: number;
      hedge_acc_eff_clean_value: boolean;
      hedge_acc_eff_cva_actual: boolean;
      hedge_acc_eff_cva_hypo: boolean;
      hedge_acc_eff_cash_flows: boolean;
      hedge_acc_eff_time_value: boolean;
      hedge_acc_eff_new_date_method: boolean;
      hedge_acc_credit_adj_values_final_balance: boolean;
    }>,
  ): Promise<AxiosResponse> {
    return await this.client.patch(
      `/v2/hedge-accounting/transaction-effectiveness/${id}/`,
      data,
    );
  }

  async deleteTransactionEffectiveness(id: number): Promise<AxiosResponse> {
    return this.client.delete(
      `/v2/hedge-accounting/transaction-effectiveness/${id}/`,
    );
  }

  async fetchTransactionRelations(params: {
    transaction_ids?: string[];
    company_entity_ids?: string[];
    run_date?: string;
  }): Promise<AxiosResponse> {
    const requestBody: {
      transaction_ids?: number[];
      company_entity_ids?: string[];
      run_date?: string;
    } = {};

    // Add transaction_ids if provided, converting to numbers
    if (params.transaction_ids && params.transaction_ids.length > 0) {
      requestBody.transaction_ids = params.transaction_ids.map((id) =>
        Number(id),
      );
    }

    // Add company_entity_ids if provided
    if (params.company_entity_ids && params.company_entity_ids.length > 0) {
      requestBody.company_entity_ids = params.company_entity_ids;
      // Per backend validation, run_date is required when company_entity_ids is provided
      requestBody.run_date = params.run_date || getTodayFormatted();
    }
    // Add run_date if provided and not already set
    else if (params.run_date) {
      requestBody.run_date = params.run_date;
    }

    return await this.client.post(
      '/v2/hedging/trans-relations/list/',
      requestBody,
    );
  }

  async fetchTransactionsWithoutHedgeAcc(params: {
    transaction_ids?: string[];
    company_entity_ids?: string[];
    run_date?: string;
  }): Promise<AxiosResponse> {
    const requestBody: any = {};

    // Add transaction_ids if provided
    if (params.transaction_ids && params.transaction_ids.length > 0) {
      requestBody.transaction_ids = params.transaction_ids.map((id) =>
        Number(id),
      );
    }

    // Add company_entity_ids if provided
    if (params.company_entity_ids && params.company_entity_ids.length > 0) {
      requestBody.company_entity_ids = params.company_entity_ids;
    }

    // Add run_date if provided, otherwise use current date
    if (params.run_date) {
      requestBody.run_date = params.run_date;
    } else {
      const today = getTodayFormatted();
      requestBody.run_date = today;
    }

    return await this.client.post(
      '/v2/hedge-accounting/transaction-no-hedge-acc/list/',
      requestBody,
    );
  }

  async createTransactionNoHedgeAcc(params: {
    transaction: number;
    percent_no_hedge_account: number;
  }): Promise<AxiosResponse> {
    return await this.client.post(
      '/v2/hedge-accounting/transaction-no-hedge-acc/',
      params,
    );
  }

  async updateTransactionNoHedgeAcc(params: {
    hedge_acc_trans_no_hedge_acc_id: number;
    transaction: number;
    percent_no_hedge_account: number;
  }): Promise<AxiosResponse> {
    return await this.client.put(
      `/v2/hedge-accounting/transaction-no-hedge-acc/${params.hedge_acc_trans_no_hedge_acc_id}/`,
      params,
    );
  }

  async getTransactionTitle(transactionId: number): Promise<AxiosResponse> {
    return await this.client.get(
      `/v2/hedging/transaction/${transactionId}/title`,
    );
  }

  async getBulkTransactionTitles(params: {
    company_entity_ids: string[];
    page?: number;
    page_size?: number;
    search_text?: string;
    instrument_type_id?: number;
    bank_entity_id?: string;
    maturity_date?: string;
    transaction_status_id?: number;
    close_out_date?: string;
    transaction_id?: number;
  }): Promise<AxiosResponse> {
    return await this.client.post(
      '/v2/hedging/transaction-titles-bulk/',
      params,
    );
  }

  async getTradesWithDeliverables(params: {
    company_entity_ids: string[];
  }): Promise<AxiosResponse> {
    return await this.client.post(
      '/v2/hedge-accounting/trades-with-deliverables/',
      params,
    );
  }

  async getTransactionDetail(transactionId: number): Promise<AxiosResponse> {
    return await this.client.get(`/v2/hedging/transaction/${transactionId}`);
  }

  async getTransactionTitleStructured(
    transactionId: number,
  ): Promise<AxiosResponse> {
    return await this.client.get(
      `/v2/hedging/transaction/${transactionId}/title-structured`,
    );
  }

  async postBulkTransactionTitlesStructured(params: {
    transaction_ids: number[];
  }): Promise<AxiosResponse> {
    return await this.client.post(
      '/v2/hedging/transaction/title-structured',
      params,
    );
  }

  async getTradeDesignationDate(transactionId: number): Promise<AxiosResponse> {
    return await this.client.get(
      `/v2/hedging/transaction/${transactionId}/trade-designation-date`,
    );
  }

  async postTradeDesignationDate(params: {
    transaction_id: number;
    designation_date: string;
  }): Promise<AxiosResponse> {
    return await this.client.post(
      `/v2/hedging/transaction/${params.transaction_id}/trade-designation-date`,
      { trade_designation_date: params.designation_date },
    );
  }

  async deleteTradeDesignationDate(
    transactionId: number,
  ): Promise<AxiosResponse> {
    return await this.client.delete(
      `/v2/hedging/transaction/${transactionId}/trade-designation-date`,
    );
  }

  async getIneffectivenessValues(transactionId: number): Promise<any> {
    const response = await this.client.get(
      `/v2/hedge-accounting/ineffectiveness-values/${transactionId}/`,
    );
    return response.data;
  }

  async postIneffectivenessValues(
    transactionId: number,
    ineffectivenessValue?: number | null,
    hypoValue?: number | null,
  ): Promise<any> {
    const response = await this.client.post(
      `/v2/hedge-accounting/ineffectiveness-values/${transactionId}/`,
      {
        actual_ineffective_initial_value: ineffectivenessValue,
        hypo_ineffective_initial_value: hypoValue,
      },
    );
    return response.data;
  }

  async getJournalEntryMovementStandards(): Promise<AxiosResponse> {
    return await this.client.get('/v2/hedge-accounting/je-movement-standard/');
  }

  async getJournalEntries(params: {
    transaction_ids?: string[];
    company_entity_ids?: string[];
  }): Promise<AxiosResponse> {
    const requestBody: {
      trade_ids: number[];
      comp_entities: string[];
    } = {
      trade_ids: [],
      comp_entities: [],
    };

    if (params.transaction_ids && params.transaction_ids.length > 0) {
      requestBody.trade_ids = params.transaction_ids.map((id) => Number(id));
    }

    if (params.company_entity_ids && params.company_entity_ids.length > 0) {
      requestBody.comp_entities = params.company_entity_ids;
    }

    return await this.client.post(
      '/v2/hedging/journal-entries/manage/',
      requestBody,
    );
  }

  async getJournalEntryMovements(
    transactionId: number,
  ): Promise<AxiosResponse> {
    return await this.client.get(
      `/v2/hedge-accounting/je-movements/?tID=${transactionId}`,
    );
  }

  async createJournalEntryMovements(params: {
    transaction_ids: number[];
    standard_id: number;
  }): Promise<AxiosResponse> {
    return await this.client.post('/v2/hedge-accounting/je-movements/', params);
  }

  async editJournalEntryMovementComment(
    transactionId: number,
    jeMovementId: number,
    comment: string,
  ): Promise<AxiosResponse> {
    return await this.client.put(
      `/v2/hedge-accounting/je-movements/${transactionId}/`,
      {
        je_movement_id: jeMovementId,
        comment: comment,
      },
    );
  }

  async getCompanyEntityHedgingStandard(
    companyEntityId: string,
  ): Promise<AxiosResponse> {
    return await this.client.get(
      `/v2/hedge-accounting/trans-hedge-accounting-standard/${companyEntityId}/`,
    );
  }

  async createOrUpdateJournalEntries(
    commands: {
      row_status: string;
      trade_id: number;
      je_date: string | null;
      je_status_id: number;
      je_comment?: string;
      entries: Array<{
        je_type: string;
        je_amount: number;
      }>;
    }[],
  ): Promise<AxiosResponse> {
    return await this.client.post(
      '/v2/hedging/journal-entries/create-update-delete/',
      { commands },
    );
  }

  async deleteTransactionAmortSetSchedule(
    hedge_acc_trans_amort_set_schedule_id: number,
  ): Promise<AxiosResponse> {
    return await this.client.delete(
      `/v2/hedge-accounting/trans-amort-set-schedule/${hedge_acc_trans_amort_set_schedule_id}/`,
    );
  }

  async updateTransactionAmortSetSchedule(
    hedge_acc_trans_amort_set_schedule_id: number,
    data: {
      hedge_acc_trans_amort_id: number;
      period_start_date: string;
      period_end_date: string;
      trans_amort_amount: number;
    },
  ): Promise<AxiosResponse> {
    return await this.client.put(
      `/v2/hedge-accounting/trans-amort-set-schedule/${hedge_acc_trans_amort_set_schedule_id}/`,
      data,
    );
  }

  async createReplicaHypothetical(
    transaction_id: string,
  ): Promise<AxiosResponse> {
    return await this.client.post('/v2/hedging/trans-relations/', {
      transaction_id,
    });
  }

  async deleteHypotheticalTransaction(
    transaction_relationship_id: string,
  ): Promise<AxiosResponse> {
    return await this.client.delete(
      `/v2/hedging/trans-relations/${transaction_relationship_id}/`,
    );
  }

  async clearTransactionRegression(
    transaction_id: number,
  ): Promise<AxiosResponse> {
    return await this.client.delete(
      `/v2/hedge-accounting/transaction-regression/transaction/${transaction_id}/`,
    );
  }

  async fetchTransactionLegs(transaction_id: number): Promise<AxiosResponse> {
    return await this.client.get(
      `/v2/hedging/transaction/${transaction_id}/legs`,
    );
  }

  async createHypoTrade(params: {
    transaction_id: number;
    delete_floor_leg?: boolean;
    create_floor_leg?: boolean;
    strike?: number;
    coupon?: number;
  }): Promise<AxiosResponse> {
    return await this.client.post('/v2/hedging/hypo-trade/create/', params);
  }

  async updateTransactionFields(params: {
    transaction_id: number;
    alias?: string;
    custom_trade_title?: string;
    transaction_comment?: string;
    display_currency_id?: number;
    je_currency_id?: number;
    is_rm_engagement?: boolean;
    tr_confirmation_ref?: string;
  }): Promise<AxiosResponse> {
    return await this.client.put(
      '/v2/hedging/transaction/update-fields',
      params,
    );
  }
}
